import React from 'react';
import {
  Box,
  Text,
  VStack,
  Heading,
  Grid,
  HStack,
  Container
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useSettings } from '../hooks/useApi';

const About = () => {
  const { data: settings, loading, error } = useSettings();

  if (loading) {
    return (
      <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
        <VStack spacing={12} maxW="5xl" mx="auto" textAlign="center">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide">
            LOADING...
          </Heading>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
        <VStack spacing={12} maxW="5xl" mx="auto" textAlign="center">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide">
            ERROR LOADING ABOUT PAGE
          </Heading>
          <Text color="gray.300">{error}</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
      <Container maxW="5xl">
        <VStack spacing={{ base: 12, md: 16, lg: 20 }} align="stretch">
          {/* Hero Section */}
          <VStack spacing={8} textAlign="center">
            <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide">
              {settings?.about_page_title || "ABOUT CLOVER RIDGE TECHNOLOGIES"}
            </Heading>
            <Text fontSize={{ base: "md", md: "lg", lg: "xl" }} color="gray.300" maxW="4xl" lineHeight="1.8">
              {settings?.about_page_subtitle ||
               "We are a remote engineering team dedicated to building scalable, reliable solutions that drive business growth and innovation."
              }
            </Text>
          </VStack>


          {/* Mission, Values & Services Grid */}
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }} gap={{ base: 6, md: 8 }}>
            {/* Mission */}
            <Box bg="gray.900" p={{ base: 6, md: 8, lg: 10 }} border="1px solid" borderColor="gray.700">
              <VStack align="start" spacing={6}>
                <HStack>
                  <Box w={3} h={3} bg="green.500" borderRadius="full" />
                  <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    OUR MISSION
                  </Heading>
                </HStack>
                <Box
                  color="gray.300"
                  lineHeight="1.8"
                  fontSize={{ base: "sm", md: "md" }}
                  dangerouslySetInnerHTML={{
                    __html: settings?.about_mission ||
                      "To empower businesses with cutting-edge cloud infrastructure that drives innovation and growth."
                  }}
                  sx={{
                    '& p': { mb: 4 },
                    '& ul': { pl: 6, mb: 4 },
                    '& ol': { pl: 6, mb: 4 },
                    '& li': { mb: 2 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      color: 'white',
                      fontWeight: 'bold',
                      mb: 3,
                      mt: 6
                    },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
            </Box>

            {/* Values */}
            <Box bg="gray.900" p={{ base: 6, md: 8, lg: 10 }} border="1px solid" borderColor="gray.700">
              <VStack align="start" spacing={6}>
                <HStack>
                  <Box w={3} h={3} bg="green.500" borderRadius="full" />
                  <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    OUR VALUES
                  </Heading>
                </HStack>
                <Box
                  color="gray.300"
                  lineHeight="1.8"
                  fontSize={{ base: "sm", md: "md" }}
                  dangerouslySetInnerHTML={{
                    __html: settings?.about_values ||
                      "We believe in excellence, innovation, and partnership in everything we do."
                  }}
                  sx={{
                    '& p': { mb: 4 },
                    '& ul': { pl: 6, mb: 4 },
                    '& ol': { pl: 6, mb: 4 },
                    '& li': { mb: 2 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      color: 'white',
                      fontWeight: 'bold',
                      mb: 3,
                      mt: 6
                    },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
            </Box>

            {/* Services */}
            <Box bg="gray.900" p={{ base: 6, md: 8, lg: 10 }} border="1px solid" borderColor="gray.700" gridColumn={{ base: "1", md: "1 / -1", lg: "3" }}>
              <VStack align="start" spacing={6}>
                <HStack>
                  <Box w={3} h={3} bg="green.500" borderRadius="full" />
                  <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    OUR SERVICES
                  </Heading>
                </HStack>
                <Box
                  color="gray.300"
                  lineHeight="1.8"
                  fontSize={{ base: "sm", md: "md" }}
                  dangerouslySetInnerHTML={{
                    __html: settings?.about_services ||
                      "We offer comprehensive cloud engineering services including architecture design, DevOps automation, security implementation, and cost optimization."
                  }}
                  sx={{
                    '& p': { mb: 4 },
                    '& ul': { pl: 6, mb: 4 },
                    '& ol': { pl: 6, mb: 4 },
                    '& li': { mb: 2 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      color: 'white',
                      fontWeight: 'bold',
                      mb: 3,
                      mt: 6
                    },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
            </Box>
          </Grid>


          {/* Stats Section - Dynamic from Admin */}
          <Grid templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }} gap={{ base: 6, md: 8 }}>
            <Box textAlign="center" p={{ base: 6, md: 8 }} bg="gray.900" border="1px solid" borderColor="gray.700">
              <VStack spacing={4}>
                <Heading size={{ base: "xl", md: "2xl" }} color="green.500">
                  {settings?.about_stat_1_number || "5+"}
                </Heading>
                <Text textTransform="uppercase" letterSpacing="wide" fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                  {settings?.about_stat_1_title || "YEARS EXPERIENCE"}
                </Text>
                <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }}>
                  {settings?.about_stat_1_description || "Building enterprise solutions"}
                </Text>
              </VStack>
            </Box>
            
            <Box textAlign="center" p={{ base: 6, md: 8 }} bg="gray.900" border="1px solid" borderColor="gray.700">
              <VStack spacing={4}>
                <Heading size={{ base: "xl", md: "2xl" }} color="green.500">
                  {settings?.about_stat_2_number || "50+"}
                </Heading>
                <Text textTransform="uppercase" letterSpacing="wide" fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                  {settings?.about_stat_2_title || "PROJECTS DELIVERED"}
                </Text>
                <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }}>
                  {settings?.about_stat_2_description || "Successful implementations"}
                </Text>
              </VStack>
            </Box>
            
            <Box textAlign="center" p={{ base: 6, md: 8 }} bg="gray.900" border="1px solid" borderColor="gray.700">
              <VStack spacing={4}>
                <Heading size={{ base: "xl", md: "2xl" }} color="green.500">
                  {settings?.about_stat_3_number || "24/7"}
                </Heading>
                <Text textTransform="uppercase" letterSpacing="wide" fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                  {settings?.about_stat_3_title || "SUPPORT AVAILABLE"}
                </Text>
                <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }}>
                  {settings?.about_stat_3_description || "Always here to help"}
                </Text>
              </VStack>
            </Box>
          </Grid>

          {/* Contact CTA Section - Dynamic from Admin */}
          <Box textAlign="center" py={{ base: 12, md: 16 }}>
            <VStack spacing={8}>
              <VStack spacing={4}>
                <Heading size={{ base: "lg", md: "xl", lg: "2xl" }} textTransform="uppercase" letterSpacing="wide">
                  {settings?.about_cta_title || "READY TO LEARN MORE?"}
                </Heading>
                <Box
                  fontSize={{ base: "md", md: "lg", lg: "xl" }}
                  color="gray.300"
                  maxW="2xl"
                  lineHeight="1.8"
                  dangerouslySetInnerHTML={{
                    __html: settings?.about_cta_description ||
                      "Let's discuss how we can help transform your cloud infrastructure and drive your business forward."
                  }}
                  sx={{
                    '& p': { mb: 4 },
                    '& ul': { pl: 6, mb: 4 },
                    '& ol': { pl: 6, mb: 4 },
                    '& li': { mb: 2 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      color: 'white',
                      fontWeight: 'bold',
                      mb: 3,
                      mt: 6
                    },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
              
              <Box>
                <Link to={settings?.about_cta_button_url || "/contact"}>
                  <Box
                    as="button"
                    bg="green.500"
                    color="white"
                    px={{ base: 8, md: 12 }}
                    py={4}
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    border="2px solid"
                    borderColor="green.500"
                    _hover={{
                      bg: "transparent",
                      color: "green.500",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(72, 187, 120, 0.3)"
                    }}
                    transition="all 0.3s ease"
                  >
                    {settings?.about_cta_button_text || "GET IN TOUCH"}
                  </Box>
                </Link>
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default About;